import { convertMicroDenomToDenom, strideAccountBalanceAtom } from '@/atoms/Wallet'
import { DataList, DataListItem, Icon, TextHeading } from '@/components'
import { STRIDE_CURRENCY } from '@/config'
import { useMediumScreen } from '@/hooks'
import illustration from '@/images/airdrop-eligible-illustration.svg'
import { fatal } from '@/utils'
import { Box, Button, Group, Space, Stack, Text, useMantineTheme } from '@mantine/core'
import BigNumber from 'bignumber.js'
import { useAtom } from 'jotai'
import Image from 'next/image'
import React from 'react'
import { useEligibleAirdropsQuery } from '@/queries'
import { useRebateProgramQuery, useTotalClaimableQuery } from '../queries'
import { AirdropContainer, AirdropCountdownTimer, AirdropGuideLink } from '../shared'
import { useAirdrop } from '../AirdropProvider'
import { AirdropRequestTokensButton } from './AirdropRequestTokensButton'

const AirdropLandingPage: React.FC = () => {
  const theme = useMantineTheme()

  const { setScreen } = useAirdrop()

  const { isMediumScreen } = useMediumScreen()

  const { data: totalClaimable } = useTotalClaimableQuery()

  const { data: rebateProgram } = useRebateProgramQuery()

  const { data: eligibleAirdrops } = useEligibleAirdropsQuery()

  if (totalClaimable == null) {
    throw fatal('Total claimable data should have been fetched by the previous screen.')
  }

  if (rebateProgram == null) {
    throw fatal('Rebate program data should have been fetched by the previous screen.')
  }

  if (eligibleAirdrops == null) {
    throw fatal('Eligible airdrops data should have been fetched by the previous screen.')
  }

  const onStart = () => {
    setScreen('tasks')
  }

  const total = new BigNumber(totalClaimable.total).plus(rebateProgram.amount)

  const [strideBalance] = useAtom(strideAccountBalanceAtom)

  return (
    <AirdropContainer>
      <Box
        sx={(t) => ({
          display: 'flex',
          flexDirection: isMediumScreen ? 'row' : 'column-reverse',
          flexWrap: isMediumScreen ? 'nowrap' : 'wrap',
          gap: isMediumScreen ? t.other.airdropWalletConnect.desktopGap : t.other.airdropWalletConnect.mobileGap,
          position: 'relative'
        })}>
        <Box sx={{ width: '100%', pointerEvents: 'none' }}>
          <Box
            sx={
              isMediumScreen
                ? { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }
                : { textAlign: 'center' }
            }>
            <Image src={illustration} alt="Airdrop Illustration" />
          </Box>
        </Box>

        <Box
          sx={(t) => ({
            flexShrink: isMediumScreen ? 0 : 1,
            maxWidth: isMediumScreen ? t.other.airdropWalletConnect.contentMaxWidth : 'auto',
            width: '100%'
          })}>
          <TextHeading sx={(t) => ({ fontSize: t.other.airdropWalletConnect.headingFontSize })}>
            You can get up to
          </TextHeading>

          <Group spacing="xs">
            <TextHeading
              sx={(t) => ({ fontSize: t.other.airdropWalletConnect.headingFontSize, color: theme.colors.pink[6] })}>
              {new BigNumber(convertMicroDenomToDenom(total)).decimalPlaces(2).toString()} {STRIDE_CURRENCY.coinDenom}
            </TextHeading>

            <Stack spacing={0}>
              <Text inline sx={(t) => ({ color: t.colors.gray[7] })}>
                this
              </Text>
              <Text inline sx={(t) => ({ color: t.colors.gray[7] })}>
                season
              </Text>
            </Stack>
          </Group>

          <Space h="md" />

          <DataList spacing="sm">
            <DataListItem label="Staked" value={<strong>{eligibleAirdrops?.join(', ')}</strong>} bold={false} />

            <DataListItem
              label="Testnet user"
              value={<AirdropEligibilityIcon value={Boolean(totalClaimable.coins.length)} />}
              bold={false}
            />

            <DataListItem
              label="Rebate program"
              value={<AirdropEligibilityIcon value={rebateProgram.status !== 'ineligible'} />}
              bold={false}
            />
          </DataList>

          <Space h="lg" />

          <Text size="md" sx={(t) => ({ color: t.colors.gray[9] })}>
            Complete some tasks this season to claim your {STRIDE_CURRENCY.coinDenom}. Current season:
          </Text>

          <Space h="md" />

          <AirdropCountdownTimer />

          <Space h="md" />

          {/* Check user balances, if 0 then redirect to faucet */}
          {strideBalance === 0 ? (
            <>
              <Text size="md" sx={(t) => ({ color: t.colors.gray[9] })}>
                First, get some STRD tokens from the faucet in order to claim your airdrop.
              </Text>

              <Space h="md" />

              <AirdropRequestTokensButton />
            </>
          ) : (
            <Button size="lg" radius="md" fullWidth onClick={onStart}>
              Start claiming
            </Button>
          )}

          <Space h="md" />

          <AirdropGuideLink />
        </Box>
      </Box>
    </AirdropContainer>
  )
}

const AirdropEligibilityIcon: React.FC<{ value: boolean }> = ({ value }) => {
  if (value) {
    return <Icon sx={(t) => ({ color: t.colors.cyan[5] })} name="checkCircle" size="xl" />
  }

  return <Icon sx={(t) => ({ color: t.colors.gray[5] })} name="crossCircle" size="xl" />
}

export { AirdropLandingPage }
